/* @flow */

import type { KeyValuePair } from '../jsHelpers/types';
import type { EulaResponse, GamestreamApiResponse, GamestreamGameConfig, Profiles, TokensResponse } from './types';
import type { GamestreamSession } from './types';

import { HttpStatus } from '../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { HttpMethod } from '../../libs/netgemLibrary/v8/types/HttpMethod';
import { buildUrl } from './helpers';

class GamestreamApi {
  static #authToken: string | null = null;
  static #backendUrl: string | null = null;
  static #refreshToken: string | null = null;

  /*
   * Getters for private members
   */

  static get authToken(): string | null {
    return this.#authToken;
  }

  static set authToken(authToken: string): void {
    this.#authToken = authToken;
  }

  static get backendUrl(): string | null {
    return this.#backendUrl;
  }

  static set backendUrl(backendUrl: string): void {
    this.#backendUrl = backendUrl;
  }

  /*
   * Static methods
   */

  static requestWithStatus = async (method: HttpMethod, endpoint: string, signal?: AbortSignal, data?: KeyValuePair<string | number | boolean>): Promise<GamestreamApiResponse> => {
    let response = null;

    try {
      if (signal?.aborted) {
        // Request aborted
        throw new Error(`Signal aborted (/${endpoint})`);
      }

      const headers = new Headers({ 'Content-Type': 'application/json' });
      if (GamestreamApi.#authToken) {
        headers.append('authorization', `Bearer ${GamestreamApi.#authToken}`);
      }

      const url = GamestreamApi.backendUrl;
      if (url === null) {
        throw new Error('Backend URL not initialized');
      }

      const options: { body?: string, headers: Headers, method: HttpMethod, signal?: AbortSignal } = {
        headers,
        method,
        signal,
      };

      if (data !== undefined) {
        options.body = JSON.stringify(data);
      }

      response = await fetch(buildUrl(url, endpoint), options);

      if (response.status === HttpStatus.NoContent) {
        // Status 204: No content
        return { response: null, status: response.status };
      }

      const responseObj = await response.json();

      const { gsStatusCode, httpStatusCode, message } = responseObj;
      if (gsStatusCode && httpStatusCode) {
        return {
          error: new Error(`Error requesting /${endpoint}: ${message}`),
          gsStatusCode,
          response: null,
          status: response.status,
        };
      }

      return { response: responseObj, status: response.status };
    } catch (error) {
      return { error, response: null, status: response?.status ?? -1 };
    }
  };

  static request = async (method: HttpMethod, endpoint: string, signal?: AbortSignal, data?: KeyValuePair<string | number | boolean>): Promise<any> => {
    const reqResponse = await GamestreamApi.requestWithStatus(method, endpoint, signal, data);

    if (reqResponse.error) {
      // Return object containing error details
      return reqResponse;
    }

    // Return success response
    return reqResponse.response ?? null;
  };

  static getTokens = async (endpoint: string, signal?: AbortSignal, data: KeyValuePair<string>): Promise<TokensResponse | GamestreamApiResponse | null> => {
    const response = await GamestreamApi.request(HttpMethod.POST, endpoint, signal, data);

    if (response === null) {
      return null;
    }

    if (response.error) {
      return response;
    }

    const { refresh_token, token } = response;

    if (typeof token === 'string' && typeof refresh_token === 'string') {
      GamestreamApi.#authToken = token;
      GamestreamApi.#refreshToken = refresh_token;
    }

    return response as TokensResponse;
  };

  // Not used as of now
  static authenticate = (username: string, password: string, signal?: AbortSignal): Promise<TokensResponse | GamestreamApiResponse | null> => {
    const data = {
      _password: password,
      _username: username,
    };

    return GamestreamApi.getTokens('front/auth', signal, data);
  };

  // Not used as of now
  static refreshAuth = (signal?: AbortSignal): Promise<TokensResponse | GamestreamApiResponse | null> | null => {
    if (!GamestreamApi.#refreshToken) {
      return null;
    }

    return GamestreamApi.getTokens('front/token/refresh', signal, { refresh_token: GamestreamApi.#refreshToken });
  };

  static getProfiles = (signal?: AbortSignal): Promise<Profiles> => GamestreamApi.request(HttpMethod.GET, 'front/profiles', signal);

  // Not used as of now
  static addProfile = async (playerPassword: string, nickname: string, maxContentRating: string, avatar: string, signal?: AbortSignal): Promise<any> =>
    await GamestreamApi.request(HttpMethod.POST, 'front/profiles', signal, { avatar, maxContentRating, nickname, playerPassword });

  // Not used as of now
  static deleteProfile = (playerPassword: string, uid: string, signal?: AbortSignal): Promise<any> => GamestreamApi.request(HttpMethod.DELETE, `front/profiles/${uid}`, signal, { playerPassword });

  // Not used as of now
  static editProfile = (playerPassword: string, uid: string, nickname: string, signal?: AbortSignal): Promise<any> =>
    GamestreamApi.request(HttpMethod.PUT, `front/profiles/${uid}`, signal, { nickname, playerPassword });

  // Not used as of now
  static getProfileGames = (profileUid: string, signal?: AbortSignal): Promise<any> => GamestreamApi.request(HttpMethod.GET, `front/my_games?profileUid=${profileUid}`, signal);

  // Not used as of now
  static getAllGames = (signal?: AbortSignal): Promise<any> => GamestreamApi.request(HttpMethod.GET, 'front/games/list', signal);

  static startSession = (data: KeyValuePair<string | number | boolean>, signal?: AbortSignal): Promise<GamestreamSession | GamestreamApiResponse> =>
    GamestreamApi.request(HttpMethod.POST, 'front/session_start', signal, data);

  static stopSession = async (profileUid: string, signal?: AbortSignal): Promise<boolean> => {
    const reqResponse = await GamestreamApi.requestWithStatus(HttpMethod.POST, 'front/session_stop', signal, { profileUid });

    if (reqResponse.error) {
      return false;
    }

    // Status 200: session closed | Status 204: no active session
    return reqResponse.status === HttpStatus.NoContent || reqResponse.status === HttpStatus.OK;
  };

  static getSession = async (profileUid: string, signal?: AbortSignal): Promise<GamestreamSession | GamestreamApiResponse | null> => {
    const reqResponse = await GamestreamApi.requestWithStatus(HttpMethod.GET, `front/session?profileUid=${profileUid}`, signal);

    if (reqResponse.error) {
      return reqResponse;
    }

    if (reqResponse.status === HttpStatus.NoContent) {
      // Status 204: No active session
      return null;
    }

    return reqResponse.response ?? null;
  };

  static getEula = (gameAlias: string, signal?: AbortSignal): Promise<EulaResponse> => GamestreamApi.request(HttpMethod.GET, `front/game/eula/${gameAlias}`, signal);

  static postEula = (gameAlias: string, signal?: AbortSignal): Promise<void> => GamestreamApi.request(HttpMethod.POST, 'front/game/eula/approveEula', signal, { alias: gameAlias });

  static getGameConfig = (sessionId: string, signal?: AbortSignal): Promise<GamestreamGameConfig | GamestreamApiResponse> =>
    GamestreamApi.request(HttpMethod.GET, `cgx/sgx_files/json/${sessionId}.sgx`, signal);
}

export default GamestreamApi;
